import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ChevronLeft, ChevronRight, FileEdit } from 'lucide-react';
import { formatDate, formatTime, formatMinutes, getWeekEnd } from './utils';

const getStatusBadge = (entryStatus) => {
  switch (entryStatus) {
    case 'clocked_in':
      return <Badge className="bg-green-500">Arbeitet</Badge>;
    case 'on_break':
      return <Badge className="bg-amber-500">Pause</Badge>;
    case 'clocked_out':
      return <Badge variant="secondary">Beendet</Badge>;
    default:
      return <Badge variant="outline">-</Badge>;
  }
};

export const WeekView = ({ selectedWeek, onChangeWeek, entries, weeklyTotals, onOpenCorrection }) => {
  return (
    <>
      <div className="flex items-center justify-center gap-2 mb-4">
        <Button variant="outline" size="icon" onClick={() => onChangeWeek(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs sm:text-sm font-medium px-2 min-w-[150px] sm:min-w-[200px] text-center">
          {formatDate(selectedWeek)} - {formatDate(getWeekEnd(selectedWeek))}
        </span>
        <Button variant="outline" size="icon" onClick={() => onChangeWeek(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
        <div className="p-2 sm:p-4 bg-secondary/30 rounded-lg text-center">
          <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Brutto</p>
          <p className="text-base sm:text-2xl font-bold font-mono break-words">{formatMinutes(weeklyTotals.work)}</p>
        </div>
        <div className="p-2 sm:p-4 bg-secondary/30 rounded-lg text-center">
          <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Pausen</p>
          <p className="text-base sm:text-2xl font-bold font-mono break-words">{formatMinutes(weeklyTotals.break)}</p>
        </div>
        <div className="p-2 sm:p-4 bg-primary/10 rounded-lg text-center">
          <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Netto</p>
          <p className="text-base sm:text-2xl font-bold font-mono text-primary break-words">{formatMinutes(weeklyTotals.net)}</p>
        </div>
      </div>

      {/* Entries Table - horizontal scroll on mobile */}
      <div className="rounded-lg border overflow-x-auto">
        <Table className="min-w-[640px]">
          <TableHeader>
            <TableRow>
              <TableHead>Tag</TableHead>
              <TableHead>Kommen</TableHead>
              <TableHead>Gehen</TableHead>
              <TableHead>Pausen</TableHead>
              <TableHead>Netto</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Keine Einträge in dieser Woche
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{formatDate(entry.date)}</TableCell>
                  <TableCell>{formatTime(entry.clock_in)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {formatTime(entry.clock_out)}
                      {entry.auto_closed && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          title={entry.auto_close_reason || 'Automatisch um 23:59 Uhr beendet'}
                          data-testid={`auto-closed-badge-${entry.id}`}
                        >
                          Auto
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{entry.total_break_minutes || 0} min</TableCell>
                  <TableCell className="font-medium">{formatMinutes(entry.net_work_minutes || 0)}</TableCell>
                  <TableCell>{getStatusBadge(entry.status)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onOpenCorrection(entry)}
                      title="Korrektur beantragen"
                    >
                      <FileEdit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
